import React from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { FocusCard } from '../FocusCard'
import type { FocusCardProps } from '../FocusCard'
import './FocusOverlay.scss'

export interface FocusOverlayProps {
  /** Alles, was die {@link FocusCard} zeigt. Ohne Fokus-Fenster gibt es keinen Überzug. */
  card: FocusCardProps
}

/**
 * **Der Fokus-Überzug** — im Web das, was auf dem Desktop das eigene Fenster
 * ist: eine Fläche über der ganzen App, in deren Mitte die {@link FocusCard}
 * steht (Kernschleife 3d).
 *
 * **Er ist streng** (Entscheidung 2026-09-16), und das ist der Unterschied zum
 * Geschafft-Moment:
 *
 * | | Geschafft-Moment | Fokus-Überzug |
 * |---|---|---|
 * | Escape | schließt | **tut nichts** |
 * | Klick auf die Fläche | schließt | **tut nichts** |
 * | Fenstersteuerung | ausgespart (`clip-path`) | zugedeckt |
 *
 * Der Moment ist eine Belohnung, die man wegwischt; der Fokus ist eine
 * Entscheidung, die man trifft. Aus ihm führt genau ein Weg heraus — **Pause**
 * — und einer hindurch: der **Haken**. Darum `modal` statt `modal={false}`: Im
 * Web gibt es keine Ampel zu schützen, und der Fokus soll den Fokus wirklich
 * haben.
 *
 * Reine Props: Was gezeigt wird, weiß die Hülle (`layouts/FocusOverlayHost`).
 *
 * @example
 * <FocusOverlay card={{ title: 'Regal bauen', lifeSpace: 'private', seconds: 300,
 *   onPause: pausieren, onComplete: erledigen }} />
 */
export const FocusOverlay: React.FC<FocusOverlayProps> = ({ card }) => (
  <Dialog.Root open modal>
    <Dialog.Portal>
      <Dialog.Overlay className="focus-overlay__veil" data-testid="focus-overlay-veil" />
      <Dialog.Content
        className="focus-overlay__shell"
        aria-describedby={undefined}
        /*
          **Escape beendet den Fokus nicht.** Ein versehentlicher Druck — oder
          einer, der eigentlich einem Menü galt — buchte sonst den Abschnitt und
          risse das Fokus-Fenster weg. Pause ist ein Knopf, und das bleibt so.
        */
        onEscapeKeyDown={event => event.preventDefault()}
        /*
          **Die Fläche ist kein Ausgang.** Anders als beim Geschafft-Moment, den
          man wegwischt: Hier liegt hinter der Fläche die App, auf die man
          gerade ausdrücklich nicht schaut.
        */
        onPointerDownOutside={event => event.preventDefault()}
        onInteractOutside={event => event.preventDefault()}
      >
        {/*
          Radix verlangt einen Titel — und der Titel der Aufgabe IST er. Ein
          zweiter, unsichtbarer daneben stünde einem Vorleser doppelt im Weg.
        */}
        <FocusCard {...card} Heading={Dialog.Title} />
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>
)
